import React, { useState, useCallback, useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export default function KioskMode({ onClose, onLogin, isReauth = false }) {
  const [digits, setDigits] = useState(['', '', '', '']);
  const [shaking, setShaking] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const timeoutRef = useRef(null);

  // Prevent physical keyboard input
  useEffect(() => {
    const handleKeyDown = (e) => {
      e.preventDefault();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Auto-submit when 4th digit is filled
  useEffect(() => {
    const allFilled = digits.every(d => d !== '');
    if (allFilled) {
      timeoutRef.current = setTimeout(() => {
        validatePIN(digits.join(''));
      }, 200);
    }
    return () => clearTimeout(timeoutRef.current);
  }, [digits]);

  const validatePIN = async (pin) => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/auth/kiosk-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ pin })
      });

      if (!res.ok) {
        let message = 'Login failed. Please try again.';
        try {
          const data = await res.json();
          if (data && data.error) message = data.error;
        } catch (_) { /* ignore parse errors */ }
        setError(message);
        triggerShake();
        return;
      }

      const data = await res.json();
      onLogin(data.user);
    } catch (err) {
      setError('Connection problem. Please try again.');
      triggerShake();
    } finally {
      setLoading(false);
    }
  };

  const triggerShake = () => {
    setShaking(true);
    setTimeout(() => {
      setShaking(false);
      setDigits(['', '', '', '']);
    }, 1200);
  };

  const handleDigitPress = useCallback((digit) => {
    if (loading) return;
    setDigits(prev => {
      const next = [...prev];
      const emptyIndex = next.indexOf('');
      if (emptyIndex !== -1) {
        next[emptyIndex] = digit;
      }
      return next;
    });
  }, [loading]);

  const handleBackspace = useCallback(() => {
    if (loading) return;
    setDigits(prev => {
      const next = [...prev];
      for (let i = 3; i >= 0; i--) {
        if (next[i] !== '') {
          next[i] = '';
          break;
        }
      }
      return next;
    });
  }, [loading]);

  const hasDigits = digits.some(d => d !== '');

  const digitSlots = digits.map((digit, i) => (
    <div
      key={i}
      className={`
        flex items-center justify-center
        rounded-2xl border-4 transition-all duration-200 font-mono font-bold
        ${shaking
          ? 'border-red-500 bg-red-50 text-red-600'
          : digit
            ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
            : 'border-gray-300 bg-white text-gray-400'
        }
      `}
      style={{ width: '11vw', height: '18vh', minHeight: '80px', fontSize: 'clamp(2rem, 5vw, 4rem)' }}
    >
      {digit}
    </div>
  ));

  const keypadButtons = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
  ];

  return (
    <div
      className="fixed inset-0 bg-gray-900 bg-opacity-95 z-[100] flex flex-col items-center justify-between py-8 px-4"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Cancel Button */}
      <button
        onClick={onClose}
        disabled={loading}
        className="absolute top-6 right-6 text-white hover:text-gray-300 text-2xl font-light z-10 disabled:opacity-50"
        title="Cancel"
      >
        ✕
      </button>

      {/* Title */}
      <div className="text-white text-xl font-light tracking-widest uppercase mt-8">
        {isReauth ? 'Session Expired - Enter Kiosk PIN' : 'Enter Kiosk PIN'}
      </div>

      {/* Error message (e.g. location not whitelisted) */}
      <div className="h-6 mt-4 px-4 text-center">
        {error && (
          <span className="inline-flex items-center gap-2 text-sm font-medium text-red-300 bg-red-500/10 border border-red-500/40 rounded-full px-4 py-1">
            {error}
          </span>
        )}
      </div>

      {/* Digit Slots */}
      <div className={`flex gap-4 sm:gap-6 flex-1 items-center ${shaking ? 'animate-shake' : ''}`}>
        {digitSlots}
      </div>

      {/* Keypad */}
      <div className="w-full max-w-md">
        {/* Number rows */}
        {keypadButtons.map((row, rowIndex) => (
          <div key={rowIndex} className="grid grid-cols-3 gap-3 mb-3">
            {row.map((digit) => (
              <button
                key={digit}
                onClick={() => handleDigitPress(digit)}
                disabled={loading}
                className="
                  h-16 sm:h-20 rounded-2xl text-3xl font-semibold
                  bg-white text-gray-900 hover:bg-gray-100 active:bg-gray-200
                  transition-colors duration-100 select-none
                  disabled:opacity-50 disabled:cursor-not-allowed
                  shadow-lg
                "
              >
                {digit}
              </button>
            ))}
          </div>
        ))}

        {/* Bottom row: Cancel / 0 / Backspace */}
        <div className="grid grid-cols-3 gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="
              h-16 sm:h-20 rounded-2xl text-lg font-semibold
              bg-red-500 text-white hover:bg-red-600 active:bg-red-700
              transition-colors duration-100 select-none
              disabled:opacity-50 disabled:cursor-not-allowed
              shadow-lg
            "
          >
            Cancel
          </button>
          <button
            onClick={() => handleDigitPress('0')}
            disabled={loading}
            className="
              h-16 sm:h-20 rounded-2xl text-3xl font-semibold
              bg-white text-gray-900 hover:bg-gray-100 active:bg-gray-200
              transition-colors duration-100 select-none
              disabled:opacity-50 disabled:cursor-not-allowed
              shadow-lg
            "
          >
            0
          </button>
          <button
            onClick={handleBackspace}
            disabled={loading || !hasDigits}
            className="
              h-16 sm:h-20 rounded-2xl text-2xl font-semibold
              bg-gray-600 text-white hover:bg-gray-500 active:bg-gray-700
              transition-colors duration-100 select-none
              disabled:opacity-30 disabled:cursor-not-allowed
              shadow-lg
            "
          >
            ←
          </button>
        </div>
      </div>

      {/* Shake animation keyframes via inline style */}
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-8px); }
          20%, 40%, 60%, 80% { transform: translateX(8px); }
        }
        .animate-shake {
          animation: shake 0.6s ease-in-out;
        }
      `}</style>
    </div>
  );
}
